import React, { Fragment, useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  fetchAgreement,
  updateAgreement,
} from "../../slices/apps/agreementSlice";

// Formik validation
import * as Yup from "yup";
import { useFormik } from "formik";

// react select
import Select from "react-select";

//Import Flatepicker
import Flatpickr from "react-flatpickr";
import { Turkish } from "flatpickr/dist/l10n/tr.js";

// diğer importlar
import ReactInputMask from "react-input-mask";

// data dosyaları
import { carsInfo } from "../../common/data/cars";

const AgreementUpdateModal = ({ id, isOpen, toggle }) => {
  const dispatch = useDispatch();

  const { agreement, agreementLoading, updateAgreementLoading, agencies } =
    useSelector((state) => {
      return {
        agreement: state.agreement.agreement,
        agreementLoading: state.agreement.agreementLoading,
        updateAgreementLoading: state.agreement.updateAgreementLoading,
        agencies: state.agency.agencies,
      };
    }, shallowEqual);

  const fetchData = async () => {
    try {
      const resultAction = await dispatch(fetchAgreement(id));

      if (resultAction.meta.requestStatus === "fulfilled") {
        agreementValidation.resetForm();
      }
    } catch (error) {
      console.error("Failed:", error);
    }
  };

  useEffect(() => {
    if (isOpen && id) {
      fetchData();
    }
  }, [isOpen, id, dispatch]);

  // Sözleşme validation
  const agreementValidation = useFormik({
    enableReinitialize: true,

    initialValues: {
      id: id,
      agencyId: agreement?.AgencyId,

      startDate: agreement?.StartDate,
      endDate: agreement?.EndDate,
      description: agreement?.Description,

      carCategory: agreement?.CarCategory,
      carBrand: agreement?.CarBrand,
      carModel: agreement?.CarModel,
      carPlate: agreement?.CarPlate,
      carYear: agreement?.CarYear,
    },

    validationSchema: Yup.object({
      startDate: Yup.string().required("Lütfen başlangıç tarihi girin"),
      endDate: Yup.string().required("Lütfen bitiş tarihi girin"),
      agencyId: Yup.string().required("Lütfen bir acente seçin"),

      carCategory: Yup.string(),
      carBrand: Yup.string(),
      carModel: Yup.string(),
      carPlate: Yup.string().required("Lütfen plaka giriniz"),
      carYear: Yup.number()
        .typeError("Model yılı girin")
        .required("Model yılı girin")
        .max(new Date().getFullYear(), `Geçersiz model yılı`)
        .test(
          "min-year-check",
          `Araç en fazla ${agreement?.CarYearLimit} yaşında olmalı`,

          function (value) {
            if (!agreement?.IsCarYearLimitEnabled) return true;
            return value >= new Date().getFullYear() - agreement?.CarYearLimit;
          }
        ),
    }),

    onSubmit: async (values) => {
      const data = {
        ...values,
        startDate: values.startDate
          ? (() => {
              const date = new Date(values.startDate);
              date.setHours(0, 0, 0, 0);
              date.setHours(date.getHours() + 3);
              return date.toISOString();
            })()
          : null,
        endDate: values.endDate
          ? (() => {
              const date = new Date(values.endDate);
              date.setHours(0, 0, 0, 0);
              date.setHours(date.getHours() + 3);
              return date.toISOString();
            })()
          : null,
      };

      const resultAction = await dispatch(updateAgreement(data));

      if (resultAction.meta.requestStatus === "fulfilled") {
        tog_large(false);
      }
    },
  });

  if (agreementLoading) {
    return null;
  }

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <ModalHeader
        className="modal-title"
        id="myLargeModalLabel"
        toggle={toggle}
      >
        Sözleşme Bilgilerini Düzenle
      </ModalHeader>

      <ModalBody>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            agreementValidation.handleSubmit();
            return false;
          }}
          action="#"
          autoComplete="off"
        >
          <Row className="align-items-start">
            <Col lg={6}>
              <div className="mb-3">
                <Label className="form-label">Başlangıç Tarihi</Label>
                <Flatpickr
                  className={
                    agreementValidation.touched.startDate &&
                    agreementValidation.errors.startDate
                      ? "form-control is-invalid"
                      : "form-control"
                  }
                  options={{
                    dateFormat: "d.m.Y",
                    // minDate: "today",
                    locale: { ...Turkish },
                  }}
                  value={
                    agreementValidation.values.startDate
                      ? new Date(agreementValidation.values.startDate)
                      : null
                  }
                  onChange={(date) => {
                    const startDate = date[0];
                    const endDate = new Date(startDate);

                    agreementValidation.setFieldValue(
                      "startDate",
                      startDate.toISOString()
                    );

                    endDate.setFullYear(endDate.getFullYear() + 1);
                    agreementValidation.setFieldValue(
                      "endDate",
                      endDate.toISOString()
                    );
                  }}
                />
                {agreementValidation.touched.startDate &&
                  agreementValidation.errors.startDate && (
                    <div type="invalid" className="d-block invalid-feedback">
                      {agreementValidation.errors.startDate}
                    </div>
                  )}
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Label className="form-label">Bitiş Tarihi</Label>
                <Flatpickr
                  className={
                    agreementValidation.touched.endDate &&
                    agreementValidation.errors.endDate
                      ? "form-control is-invalid"
                      : "form-control"
                  }
                  options={{
                    dateFormat: "d.m.Y",
                    // minDate: "today",
                    locale: { ...Turkish },
                  }}
                  value={
                    agreementValidation.values.endDate
                      ? new Date(agreementValidation.values.endDate)
                      : null
                  }
                  onChange={(date) => {
                    const endDate = date[0];

                    agreementValidation.setFieldValue(
                      "endDate",
                      endDate.toISOString()
                    );
                  }}
                />
                {agreementValidation.touched.endDate &&
                  agreementValidation.errors.endDate && (
                    <div type="invalid" className="d-block invalid-feedback">
                      {agreementValidation.errors.endDate}
                    </div>
                  )}
              </div>
            </Col>

            <Col lg={12}>
              <div className="mb-3">
                <Label htmlFor="agencyIdSelect" className="form-label">
                  Acente
                </Label>
                <Select
                  placeholder="Acente seçiniz"
                  id="agencyIdSelect"
                  name="agencyId"
                  options={agencies.map((item) => ({
                    label: item.CompanyName,
                    value: item.Id,
                  }))}
                  noOptionsMessage={() => "Seçenek bulunamadı"}
                  value={
                    agencies
                      .map((item) => ({
                        label: item.CompanyName,
                        value: item.Id,
                      }))
                      .find(
                        (item) =>
                          item.value === agreementValidation.values.agencyId
                      ) || ""
                  }
                  onChange={(value) => {
                    agreementValidation.setFieldValue("agencyId", value.value);
                  }}
                  onBlur={agreementValidation.handleBlur}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor:
                        agreementValidation.touched.agencyId &&
                        agreementValidation.errors.agencyId &&
                        !state.isFocused
                          ? "#e30a17"
                          : "#e9ebec",
                    }),
                  }}
                />
                {agreementValidation.touched.agencyId &&
                  agreementValidation.errors.agencyId && (
                    <div className="d-block invalid-feedback">
                      {agreementValidation.errors.agencyId}
                    </div>
                  )}
              </div>
            </Col>

            <Col lg={12}>
              <div className="mb-3">
                <Label htmlFor="description" className="form-label">
                  Açıklama
                </Label>
                <Input
                  name="description"
                  className="form-control"
                  placeholder="Açıklama"
                  type="text"
                  autoComplete="off"
                  onChange={agreementValidation.handleChange}
                  onBlur={agreementValidation.handleBlur}
                  value={agreementValidation.values.description || ""}
                  invalid={
                    agreementValidation.touched.description &&
                    agreementValidation.errors.description
                      ? true
                      : false
                  }
                />
                {agreementValidation.touched.description &&
                  agreementValidation.errors.description && (
                    <FormFeedback type="invalid">
                      {agreementValidation.errors.description}
                    </FormFeedback>
                  )}
              </div>
            </Col>

            <Col lg={12}>
              <div className="mb-3">
                <Label htmlFor="carPlate" className="form-label">
                  Plaka
                </Label>
                <Input
                  name="carPlate"
                  className="form-control"
                  placeholder="Plaka"
                  type="text"
                  autoComplete="off"
                  maxLength={50}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    agreementValidation.setFieldValue("carPlate", value);
                  }}
                  onBlur={agreementValidation.handleBlur}
                  value={agreementValidation.values.carPlate || ""}
                  invalid={
                    agreementValidation.touched.carPlate &&
                    agreementValidation.errors.carPlate
                      ? true
                      : false
                  }
                />
                {agreementValidation.touched.carPlate &&
                  agreementValidation.errors.carPlate && (
                    <FormFeedback type="invalid">
                      {agreementValidation.errors.carPlate}
                    </FormFeedback>
                  )}
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="carCategorySelect" className="form-label">
                  Araç Tipi
                </Label>
                <Select
                  placeholder="Araç tipi seçiniz"
                  id="carCategorySelect"
                  name="carCategory"
                  options={carsInfo.map((item) => ({
                    value: item.typeName,
                    label: item.typeName,
                  }))}
                  noOptionsMessage={() => "Seçenek bulunamadı"}
                  isClearable={true}
                  value={
                    carsInfo
                      .map((item) => ({
                        value: item.typeName,
                        label: item.typeName,
                      }))
                      .find(
                        (option) =>
                          option.value ===
                          agreementValidation.values.carCategory
                      ) || ""
                  }
                  onChange={(value) => {
                    agreementValidation.setFieldValue(
                      "carCategory",
                      value ? value.value : ""
                    );
                    agreementValidation.setFieldValue("carBrand", "");
                  }}
                  onBlur={agreementValidation.handleBlur}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor:
                        agreementValidation.touched.carCategory &&
                        agreementValidation.errors.carCategory &&
                        !state.isFocused
                          ? "#e30a17"
                          : "#e9ebec",
                    }),
                  }}
                />
                {agreementValidation.touched.carCategory &&
                  agreementValidation.errors.carCategory && (
                    <div className="d-block invalid-feedback">
                      {agreementValidation.errors.carCategory}
                    </div>
                  )}
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Label className="form-label" htmlFor="carYear">
                  Model Yılı
                </Label>
                <InputGroup>
                  <ReactInputMask
                    mask="9999"
                    maskChar=" "
                    name="carYear"
                    placeholder="Model Yılı"
                    inputMode="numeric"
                    className={
                      agreementValidation.touched.carYear &&
                      agreementValidation.errors.carYear
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    onChange={agreementValidation.handleChange}
                    value={agreementValidation.values.carYear || ""}
                    style={{ zIndex: 0 }}
                  />
                </InputGroup>
                {agreementValidation.touched.carYear &&
                  agreementValidation.errors.carYear && (
                    <div type="invalid" className="d-block invalid-feedback">
                      {agreementValidation.errors.carYear}
                    </div>
                  )}
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="carBrandSelect" className="form-label">
                  Marka
                </Label>
                <Select
                  placeholder="Marka seçiniz"
                  id="carBrandSelect"
                  name="carBrand"
                  options={
                    carsInfo
                      .find(
                        (item) =>
                          item.typeName ===
                          agreementValidation.values.carCategory
                      )
                      ?.brands.map((brand) => ({
                        value: brand.brandName,
                        label: brand.brandName,
                      })) || []
                  }
                  noOptionsMessage={() => "Seçenek bulunamadı"}
                  isClearable={true}
                  value={
                    carsInfo
                      .find(
                        (item) =>
                          item.typeName ===
                          agreementValidation.values.carCategory
                      )
                      ?.brands.map((brand) => ({
                        value: brand.brandName,
                        label: brand.brandName,
                      }))
                      .find(
                        (option) =>
                          option.value === agreementValidation.values.carBrand
                      ) || ""
                  }
                  onChange={(value) => {
                    agreementValidation.setFieldValue(
                      "carBrand",
                      value ? value.value : ""
                    );
                  }}
                  onBlur={agreementValidation.handleBlur}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor:
                        agreementValidation.touched.carBrand &&
                        agreementValidation.errors.carBrand &&
                        !state.isFocused
                          ? "#e30a17"
                          : "#e9ebec",
                    }),
                  }}
                />
                {agreementValidation.touched.carBrand &&
                  agreementValidation.errors.carBrand && (
                    <div className="d-block invalid-feedback">
                      {agreementValidation.errors.carBrand}
                    </div>
                  )}
              </div>
            </Col>

            <Col lg={6}>
              <div className="mb-3">
                <Label htmlFor="carModel" className="form-label">
                  Model
                </Label>
                <Input
                  name="carModel"
                  className="form-control"
                  placeholder="Model"
                  type="text"
                  autoComplete="off"
                  onChange={agreementValidation.handleChange}
                  onBlur={agreementValidation.handleBlur}
                  value={agreementValidation.values.carModel || ""}
                  invalid={
                    agreementValidation.touched.carModel &&
                    agreementValidation.errors.carModel
                      ? true
                      : false
                  }
                />
                {agreementValidation.touched.carModel &&
                  agreementValidation.errors.carModel && (
                    <FormFeedback type="invalid">
                      {agreementValidation.errors.carModel}
                    </FormFeedback>
                  )}
              </div>
            </Col>
          </Row>

          <div className="d-flex align-items-center gap-3 mt-4">
            <Button
              className="btn btn-sm ms-auto nexttab nexttab"
              color="primary"
              type="submit"
              disabled={updateAgreementLoading}
            >
              Kaydet
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default AgreementUpdateModal;
